import React from 'react';
import { X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { HealthStatus } from '../types/health';

interface HealthStatusModalProps {
  healthStatus: HealthStatus | null;
  error: string | null;
  onClose: () => void;
}

export const HealthStatusModal: React.FC<HealthStatusModalProps> = ({
  healthStatus,
  error,
  onClose,
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'unhealthy':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div data-component="HealthStatusModal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">System Health Status</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {error ? (
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          ) : healthStatus ? (
            <>
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(healthStatus.status)}
                  <span className="font-medium text-gray-900 capitalize">
                    {healthStatus.status}
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  Last checked: {formatTimestamp(healthStatus.timestamp)}
                </p>
              </div>

              <div className="space-y-3">
                {Object.entries(healthStatus.components).map(([name, component]) => (
                  <div
                    key={name}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    title={component.message || ''}
                  >
                    {getStatusIcon(component.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {name}
                      </p>
                      {component.message && (
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {component.message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Loading health status...</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-600 text-center">
            Health checks are cached for 10 minutes
          </p>
        </div>
      </div>
    </div>
  );
};
